import {
  Component,
  computed,
  inject,
  OnDestroy,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { AppRoutes } from '@helpers/app-routes';
import { OrderService } from '@services/order.service';
import { CustomerService } from '@services/customer.service';
import { UserCompanyDataService } from '@services/user-company-data.service';
import { SummaryCourseCardComponent } from '@components/summary-course-card/summary-course-card.component';
import { FormatPricePipe } from '@pipes/format-price.pipe';
import { SessionOrderService } from '@services/session-order.service';
import { SessionService } from '@services/session.service';
import { TerminalService } from '@services/terminal.service';
import { MealPlanService } from '@services/meal-plan.service';
import { MealmomentService } from '@services/meal-moment.service';
import { PendingOrderService } from '@services/pending-order.service';
import { SessionOrderCreateModel } from '@models/session-order/session-order.model';
import { OrderCreateModel } from '@models/order/order.model';
import { OrderCartModel } from '@models/order/order-cart.model';
import { PaymentType } from '@models/user-company-data/user-company-data.model';
import { UserModel } from '@models/user/user.model';
import { MealPlanModel } from '@models/meal-plan/meal-plan.model';
import { ModalConfirmationComponent } from '@components/Modals/modal-confirmation/modal-confirmation.component';
import { ModalInsufficientBalanceComponent } from '@components/Modals/modal-insufficient-balance/modal-insufficient-balance.component';
import { ModalForceReasonComponent } from '@components/Modals/modal-force-reason/modal-force-reason.component';
import { CompanyService } from '@services/company.service';

@Component({
  selector: 'app-order-summary-page',
  standalone: true,
  imports: [
    TranslateModule,
    SummaryCourseCardComponent,
    FormatPricePipe,
    ModalConfirmationComponent,
    ModalInsufficientBalanceComponent,
    ModalForceReasonComponent,
  ],
  templateUrl: './order-summary-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  host: { class: 'flex h-full bg-white' },
})
export class OrderSummaryPageComponent implements OnDestroy {
  private readonly router = inject(Router);
  private readonly toastr = inject(ToastrService);
  private readonly translate = inject(TranslateService);
  private readonly orderService = inject(OrderService);
  private readonly customerService = inject(CustomerService);
  private readonly userCompanyDataService = inject(UserCompanyDataService);
  private readonly sessionOrderService = inject(SessionOrderService);
  private readonly sessionService = inject(SessionService);
  private readonly terminalService = inject(TerminalService);
  private readonly mealPlanService = inject(MealPlanService);
  private readonly mealmomentService = inject(MealmomentService);
  private readonly companyService = inject(CompanyService);
  private readonly pendingOrderService = inject(PendingOrderService);

  private readonly company = computed(() => this.companyService.currentCompany());
  #autoNavigateTimeout: ReturnType<typeof setTimeout> | null = null;

  readonly modalData = signal<{ customerName: string } | null>(null);
  readonly insufficientBalanceModalOpen = signal(false);
  readonly forceReasonModalOpen = signal(false);
  readonly order = computed(() => this.orderService.currentOrder());
  readonly customer = computed(() => this.customerService.currentCustomer());
  readonly sessionId = computed(() => this.sessionService.lastSession()?.id ?? null);
  readonly mealPlan = computed(() => this.mealPlanService.currentMealPlan());
  readonly countdownSeconds = 3;

  readonly paymentTypeKey = computed(() => {
    const type = this.userCompanyDataService.currentUserCompanyData()?.payment_type;
    return type ? `payment_type.${type.toLowerCase()}` : null;
  });

  readonly walletBalance = computed(() => {
    const currency = this.customer()?.wallet?.currency;
    return currency != null ? currency / 100 : 0;
  });

  readonly balanceAfterDebit = computed(
    () => this.walletBalance() - (this.order()?.totalPrice ?? 0),
  );

  readonly paymentType = computed(
    () => this.userCompanyDataService.currentUserCompanyData()?.payment_type,
  );

  readonly canPayDirectly = computed(
    () =>
      this.walletBalance() > (this.order()?.totalPrice ?? 0) ||
      this.paymentType()?.toLowerCase() === PaymentType.SALARY,
  );

  readonly sections = computed(() => {
    const order = this.order();
    if (!order) return [];
    if (order.isMealPlanMode) {
      return [
        {
          titleKey: 'order_summary.formula_section',
          items: order.items.filter((item) => !item.part.service_is_not_in_admission),
          showPrice: false,
        },
        {
          titleKey: 'order_summary.supplement_section',
          items: order.items.filter((item) => item.part.service_is_not_in_admission),
          showPrice: true,
        },
      ].filter((s) => s.items.length > 0);
    }
    return [
      {
        titleKey: 'order_summary.by_course_section',
        items: order.items,
        showPrice: true,
      },
    ];
  });

  readonly balanceColor = computed(() => {
    const balance = this.balanceAfterDebit();
    if (balance > 0) return 'fg-vert-1';
    if (balance < 0) return 'fg-rose-1';
    return 'fg-gris-1';
  });

  onModalButtonClick(): void {
    this.#closeModal();
  }

  ngOnDestroy(): void {
    if (this.#autoNavigateTimeout !== null) {
      clearTimeout(this.#autoNavigateTimeout);
    }
  }

  #closeModal(): void {
    if (this.#autoNavigateTimeout !== null) {
      clearTimeout(this.#autoNavigateTimeout);
      this.#autoNavigateTimeout = null;
    }
    this.modalData.set(null);
    void this.router.navigate([AppRoutes.scanPage]);
  }

  removeItem(courseId: number): void {
    this.orderService.removeItem(courseId);
  }

  confirmOrder(): void {
    if (this.canPayDirectly()) {
      this.#submitOrder(false, null);
      return;
    }
    this.insufficientBalanceModalOpen.set(true);
  }

  onBackToSelection(): void {
    this.insufficientBalanceModalOpen.set(false);
  }

  onSave(): void {
    this.insufficientBalanceModalOpen.set(false);
    const order = this.order();
    const customer = this.customer();
    if (order && customer) {
      this.pendingOrderService.save(customer.id, order, this.mealPlan());
    }
    void this.router.navigate([AppRoutes.scanPage]);
  }

  onForcePassage(): void {
    this.insufficientBalanceModalOpen.set(false);
    this.forceReasonModalOpen.set(true);
  }

  onCancelForceReason(): void {
    this.forceReasonModalOpen.set(false);
    this.insufficientBalanceModalOpen.set(true);
  }

  onConfirmForceReason(reason: string): void {
    this.forceReasonModalOpen.set(false);
    this.#submitOrder(true, reason);
  }

  #buildOrderPayload(
    order: OrderCartModel,
    customer: UserModel,
    mealPlan: MealPlanModel | null,
  ): OrderCreateModel {
    return {
      admission_id: mealPlan?.admission_id ?? null,
      user_id: customer.id,
      enterprise_id: this.terminalService.currentTerminal()!.enterprise_id,
      mealmoment_id: mealPlan?.meal_moment.id ?? this.mealmomentService.currentMealMoment()!.id,
      company_id: this.company()!.id,
      delivery_date: new Date().toISOString().split('T')[0],
      number_of_admission: 1,
      payment_ways:
        this.paymentType()?.toLowerCase() === PaymentType.WALLET
          ? [
              {
                WALLET: this.balanceAfterDebit(),
              },
            ]
          : [
              {
                SALARY: order.totalPrice,
              },
            ],
      is_order_for_group: false,
      service_id: order.items[0].part.service_id,
      order_items: order.items.map((item) => ({
        plat_id: item.course.id,
        quantity: item.quantity,
        service_content: item.part.service_name,
        is_extra: item.course.is_extra,
        price: item.course.price ?? 0,
        vat: item.course.vat ?? 0,
        gpao_id: item.course.gpao_id?.toString() ?? null,
        service_id: item.part.service_id,
      })),
    };
  }

  #buildSessionOrderPayload(
    orderPayload: OrderCreateModel,
    customerId: number,
    isForced: boolean,
    forcedComment: string | null,
  ): SessionOrderCreateModel {
    return {
      self_pos_terminal_session_id: this.sessionId()!,
      user_id: customerId,
      order: orderPayload,
      is_validated: true,
      is_forced: isForced,
      forced_comment: forcedComment,
    };
  }

  #submitOrder(isForced: boolean, forcedComment: string | null): void {
    if (!this.sessionId()) {
      this.toastr.error(
        this.translate.instant('order_summary.toast.no_session_message'),
        this.translate.instant('order_summary.toast.no_session_title'),
      );
      return;
    }
    const order = this.order()!;
    const customer = this.customer()!;
    const mealPlan = this.mealPlan();

    const orderPayload = this.#buildOrderPayload(order, customer, mealPlan);
    const sessionOrder = this.#buildSessionOrderPayload(
      orderPayload,
      customer.id,
      isForced,
      forcedComment,
    );
    this.sessionOrderService.createSessionOrder(sessionOrder).subscribe({
      next: () => {
        this.toastr.success(
          this.translate.instant('order_summary.toast.success_message'),
          this.translate.instant('order_summary.toast.success_title'),
        );
        this.orderService.clear();
        this.modalData.set({ customerName: `${customer.firstname} ${customer.lastname}` });
        this.#autoNavigateTimeout = setTimeout(
          () => this.#closeModal(),
          this.countdownSeconds * 1000,
        );
      },
      error: (error) => {
        const translatedMessage = this.translate.instant(error.message);

        if (translatedMessage !== error.message) {
          this.toastr.error(
            translatedMessage,
            this.translate.instant('order_summary.toast.error_title'),
          );
        } else {
          this.toastr.error(
            this.translate.instant('order_summary.toast.error_message'),
            this.translate.instant('order_summary.toast.error_title'),
          );
        }
      },
    });
  }
}
